import { ReactNode, useState, useMemo, SetStateAction, Dispatch } from 'react';
import BigNumber from 'bignumber.js';

import {
  AccountState,
  useAccountState,
} from '#/features/account/account-context';
import { useSystemConfig } from '#/features/system/system-config-context';

import createSafeContext from '#/utils/createSafeContext';

import fieldErrorsView from '#/views/field-errors';
import { BridgedToken } from '../paraclear';

export interface WithdrawFormState {
  readonly amount: BigNumber | null;
  readonly asset: string;
}

export interface WithdrawFormView {
  readonly amount: FieldView;
  readonly asset: AssetView;

  // Derived
  readonly isValid: boolean;
  readonly error: string;
}

type AssetView = {
  readonly name: string;
  readonly symbol: string;
};

export interface WithdrawFormActions {
  readonly updateAmount: (amount: string) => void;
  readonly reset: () => void;
}

export const INITIAL_STATE: WithdrawFormState = {
  amount: null,
  asset: 'USDC',
};

export const [useWithdrawFormView, ViewCtx] =
  createSafeContext<WithdrawFormView>('WithdrawFormView');
export const [useWithdrawFormActions, ActionsCtx] =
  createSafeContext<WithdrawFormActions>('WithdrawFormActions');

interface ProviderProps {
  readonly children: ReactNode;
}

export function WithdrawFormProvider({ children }: ProviderProps) {
  const [formState, setFormState] = useState(INITIAL_STATE);
  const accountState = useAccountState();
  const config = useSystemConfig();
  const token = config.getToken(formState.asset);

  const actions = useMemo(() => createActions(setFormState), [setFormState]);
  const view = useMemo(
    () => createView(formState, accountState, token),
    [formState, accountState, token],
  );

  return (
    <ActionsCtx value={actions}>
      <ViewCtx value={view}>{children}</ViewCtx>
    </ActionsCtx>
  );
}

interface FieldView {
  readonly isValid: boolean;
  readonly error: string;
  /**
   * Raw value
   * @example '30000.5'
   * @example '0.34'
   */
  readonly value: string;
  /**
   * Formatted value
   * @example '30,000.50'
   */
  readonly formatted: string;
}

export function createView(
  formState: WithdrawFormState,
  accountState: AccountState,
  token: BridgedToken,
): WithdrawFormView {
  const amount = amountView(formState, accountState, token);
  const asset = { name: token.name, symbol: token.symbol };
  const isValid = [amount].every((field) => field.isValid);
  const error = fieldErrorsView([amount]);
  return { amount, asset, isValid, error };
}

function amountView(
  state: WithdrawFormState,
  accountState: AccountState,
  token: BridgedToken,
): WithdrawFormView['amount'] {
  const { amount } = state;
  if (amount == null) {
    return {
      value: '',
      formatted: '-',
      isValid: false,
      error: '',
    };
  }

  const value = amount.toString();
  const formatted = `${amount.toFormat(2)} ${token.symbol}`;

  const freeCollateral = accountState.account?.free_collateral;

  if (freeCollateral != null && freeCollateral.isLessThanOrEqualTo(0)) {
    return {
      value,
      formatted,
      isValid: false,
      error:
        'You have no funds to withdraw. It is not possible' +
        ' to withdraw more than your free collateral.',
    };
  }

  if (freeCollateral != null && amount.isGreaterThan(freeCollateral)) {
    return {
      value,
      formatted,
      isValid: false,
      error: 'It is not possible to withdraw more than your free collateral.',
    };
  }

  if (amount.isLessThanOrEqualTo(0)) {
    return {
      value,
      formatted,
      isValid: false,
      error: '',
    };
  }

  return {
    value,
    formatted,
    isValid: true,
    error: '',
  };
}

export function createActions(
  setState: Dispatch<SetStateAction<WithdrawFormState>>,
): WithdrawFormActions {
  const updateAmount = (amount: string) => {
    const parsed = new BigNumber(amount);
    setState((state) => ({
      ...state,
      amount: parsed.isFinite() ? parsed : null,
    }));
  };
  const reset = () => {
    setState(() => INITIAL_STATE);
  };

  return {
    updateAmount,
    reset,
  };
}
